import * as XLSX from "xlsx-js-style";
import { saveAs } from "file-saver";
import { getEvaluateWeight, getScoreLevelConfig } from "./evaluateConfig";

// น้ำหนักแต่ละแถวไม่เท่ากันได้ (70/30 ปกติ, 50/50 ถ้ายังไม่ผ่านการทดลองงาน)
// จึงแยกเป็นคอลัมน์ "น้ำหนัก" แทนการฝัง % ไว้ในหัวคอลัมน์
const EXPORT_HEADERS = [
  "ลำดับ",
  "เจ้าหน้าที่",
  "น้ำหนัก (อ.1/อ.2)",
  "องค์ประกอบที่ 1 : ผลสัมฤทธิ์ของงาน",
  "องค์ประกอบที่ 2 : พฤติกรรมการปฏิบัติราชการ(สมรรถนะ)",
  "รวมคะแนน 100%",
  "ระดับผลการประเมิน",
];

const COLUMN_WIDTHS = [
  { wch: 8 },
  { wch: 32 },
  { wch: 18 },
  { wch: 34 },
  { wch: 46 },
  { wch: 16 },
  { wch: 20 },
];

const BORDER_LINE = { style: "thin", color: { rgb: "AAAAAA" } };
const CELL_BORDER = {
  top: BORDER_LINE,
  bottom: BORDER_LINE,
  left: BORDER_LINE,
  right: BORDER_LINE,
};

const TITLE_STYLE = {
  font: { sz: 16, bold: true },
  alignment: { horizontal: "center", vertical: "center" },
};

const HEADER_STYLE = {
  font: { sz: 12, bold: true, color: { rgb: "FFFFFF" } },
  fill: { fgColor: { rgb: "34699A" } },
  alignment: { horizontal: "center", vertical: "center", wrapText: true },
  border: CELL_BORDER,
};

const TEXT_STYLE = {
  alignment: { horizontal: "left", vertical: "center", wrapText: true },
  border: CELL_BORDER,
};

const CENTER_STYLE = {
  alignment: { horizontal: "center", vertical: "center" },
  border: CELL_BORDER,
};

const SCORE_STYLE = {
  numFmt: "0.0000",
  alignment: { horizontal: "center", vertical: "center" },
  border: CELL_BORDER,
};

const round4 = (value) => Math.round(value * 10000) / 10000;

const pickScore = (...values) => {
  const found = values.find(v => v !== null && v !== undefined && v !== "");
  return found === undefined ? null : Number(found);
};

// คิดคะแนนแบบเดียวกับแบบฟอร์มที่ 3 คือ คะแนนน้ำหนัก(ก) x สัดส่วนคะแนน(ข) ของแต่ละองค์ประกอบ
// แล้วนำผลของทั้งสององค์ประกอบมารวมกันเป็นคะแนนรวม 100%
export const buildEvaluateExportRow = (item, index) => {
  const salaryData = item.evaluateSalaryData ?? {};
  const firstScore = pickScore(item.first_score, salaryData.first_score);
  const secondScore = pickScore(item.second_score, salaryData.second_score);
  const storedTotal = pickScore(item.total_score, salaryData.total_score, salaryData.show_score);

  const weight = getEvaluateWeight(item);
  const weightedFirst = firstScore === null ? null : (firstScore * Number(weight.first)) / 100;
  const weightedSecond = secondScore === null ? null : (secondScore * Number(weight.second)) / 100;
  const totalScore = storedTotal !== null ? storedTotal : (weightedFirst ?? 0) + (weightedSecond ?? 0);
  const hasScore = firstScore !== null || secondScore !== null || storedTotal !== null;

  return [
    index + 1,
    item.user_name ?? "",
    `${weight.first}/${weight.second}`,
    weightedFirst === null ? "" : round4(weightedFirst),
    weightedSecond === null ? "" : round4(weightedSecond),
    hasScore ? round4(totalScore) : "",
    hasScore ? getScoreLevelConfig({ total_score: totalScore })?.text ?? "" : "",
  ];
};

const styleForColumn = (columnIndex, value) => {
  if (columnIndex === 1) return TEXT_STYLE;
  // ลำดับ / น้ำหนัก / ระดับผลการประเมิน = ข้อความจัดกึ่งกลาง, ที่เหลือเป็นคะแนนทศนิยม 4 ตำแหน่ง
  if (columnIndex === 0 || columnIndex === 2 || columnIndex === 6) return CENTER_STYLE;
  return value === "" ? CENTER_STYLE : SCORE_STYLE;
};

export const exportEvaluateExcel = (dataInput = [], { budgetYear, round } = {}) => {
  const rows = dataInput.map((item, index) => buildEvaluateExportRow(item, index));
  const title = `สรุปผลการประเมินผลการปฏิบัติราชการ รอบการประเมินที่ ${round ?? "-"} ปี ${budgetYear ?? "-"}`;

  const worksheet = XLSX.utils.aoa_to_sheet([[title], EXPORT_HEADERS, ...rows]);

  worksheet["!cols"] = COLUMN_WIDTHS;
  worksheet["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: EXPORT_HEADERS.length - 1 } }];
  worksheet["!rows"] = [{ hpt: 26 }, { hpt: 46 }];
  worksheet["!autofilter"] = {
    ref: XLSX.utils.encode_range({
      s: { r: 1, c: 0 },
      e: { r: rows.length + 1, c: EXPORT_HEADERS.length - 1 },
    }),
  };

  worksheet[XLSX.utils.encode_cell({ r: 0, c: 0 })].s = TITLE_STYLE;
  EXPORT_HEADERS.forEach((_, columnIndex) => {
    worksheet[XLSX.utils.encode_cell({ r: 1, c: columnIndex })].s = HEADER_STYLE;
  });
  rows.forEach((row, rowIndex) => {
    row.forEach((value, columnIndex) => {
      const cell = worksheet[XLSX.utils.encode_cell({ r: rowIndex + 2, c: columnIndex })];
      if (cell) {
        cell.s = styleForColumn(columnIndex, value);
      }
    });
  });

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "สรุปผลการประเมิน");
  const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
  const blob = new Blob([excelBuffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
  });
  saveAs(blob, `สรุปผลการประเมินผลการปฏิบัติราชการ_รอบที่${round ?? "-"}_ปี${budgetYear ?? "-"}.xlsx`);
};

export default exportEvaluateExcel;
